// src/stations/sprint/interleaver.ts — Interleaving Engine (FR-INTER-001, FR-INTER-002)
// Implements cognitive strategy discrimination by mixing 2-3 sub-topics per session
// and strictly enforcing the constraint: no more than 2 consecutive questions from the same sub-topic.

export interface InterleaverOptions {
  /** Maximum consecutive questions allowed from the same sub-topic (default: 2) */
  maxConsecutiveSameTopic?: number;
}

/**
 * Interleaves a question pool across distinct sub-topics.
 * Prevents "blocked practice" illusion of competence and fosters strategy discrimination.
 * Invariant: Never allows > maxConsecutiveSameTopic (default 2) from the same topic in sequence.
 */
export function interleaveQuestions<T extends { topic: string }>(
  questions: T[],
  options: InterleaverOptions = {}
): T[] {
  if (questions.length <= 2) {
    return [...questions];
  }

  const maxConsecutive = options.maxConsecutiveSameTopic ?? 2;

  // 1. Group questions by topic
  const topicBuckets = new Map<string, T[]>();
  for (const q of questions) {
    const key = q.topic.trim();
    const bucket = topicBuckets.get(key) ?? [];
    bucket.push(q);
    topicBuckets.set(key, bucket);
  }

  // If only 1 topic exists, cannot interleave across topics
  if (topicBuckets.size <= 1) {
    return [...questions];
  }

  const result: T[] = [];
  let lastTopic: string | null = null;
  let currentConsecutive = 0;

  const totalQuestions = questions.length;

  while (result.length < totalQuestions) {
    // Find all topics that still have questions remaining
    const candidateTopics = Array.from(topicBuckets.entries())
      .filter(([_, items]) => items.length > 0)
      .sort((a, b) => b[1].length - a[1].length); // Largest bucket first

    if (candidateTopics.length === 0) break;

    // Filter candidates by consecutive limit constraint
    let eligible = candidateTopics;
    if (currentConsecutive >= maxConsecutive && lastTopic !== null) {
      const filtered = candidateTopics.filter(([topic]) => topic !== lastTopic);
      if (filtered.length > 0) {
        eligible = filtered;
      }
    }

    // Prefer a topic different from lastTopic if possible
    let chosenEntry = eligible.find(([topic]) => topic !== lastTopic);
    if (!chosenEntry) {
      chosenEntry = eligible[0]!;
    }

    const [chosenTopic, items] = chosenEntry;
    const nextItem = items.shift()!;
    result.push(nextItem);

    if (chosenTopic === lastTopic) {
      currentConsecutive += 1;
    } else {
      lastTopic = chosenTopic;
      currentConsecutive = 1;
    }
  }

  return result;
}

/** Check if sub-topic has changed between two questions (FR-INTER-002) */
export function isSubTopicTransition<T extends { topic: string }>(
  previous: T | null,
  current: T
): boolean {
  if (!previous) return false;
  return previous.topic.trim().toLowerCase() !== current.topic.trim().toLowerCase();
}
